import { useCallback, useState } from 'react';

import { useTracking } from '@change-corgi/core-react/tracking';

import { useLocationSearch } from '#src/app/pages/search/shared/hooks';
import type { LocationItem } from '#src/app/pages/search/shared/types';

type Result = ModelHookResult<
	{
		data: {
			isLoading: boolean;
			isError: boolean;
			locations: readonly LocationItem[];
			selectedKey: string;
		};
		searchTerm: string;
	},
	{
		handleInputFocus: () => void;
		handleInputChange: (value: string) => void;
		handleInputBlur: () => void;
		selectLocation: (searchValue: string) => void;
	}
>;

export function useLocationFilter(): Result {
	const track = useTracking();

	const {
		data: { locations, isLoading, isError, selectedLocation },
		actions: { searchDebounced, selectLocation },
	} = useLocationSearch();

	const [localSearchTerm, setLocalSearchTerm] = useState<string | null>(null);
	const searchTerm = localSearchTerm ?? (selectedLocation || '');

	return {
		data: {
			data: {
				isLoading,
				isError,
				locations,
				// an old `?city=London` link selects the option "London" was merged into
				selectedKey:
					locations.find(
						({ value, mergedValues }) =>
							!!selectedLocation && (value === selectedLocation || mergedValues?.includes(selectedLocation)),
					)?.value ||
					selectedLocation ||
					'',
			},
			searchTerm,
		},
		actions: {
			handleInputFocus: useCallback(() => {
				void track('search_location_filter_enter');
			}, [track]),
			handleInputChange: useCallback(
				(value: string) => {
					if (!value) {
						setLocalSearchTerm('');
						return;
					}
					void track('search_location_filter_search', { search_term: value });
					searchDebounced(value);
					setLocalSearchTerm(value);
				},
				[searchDebounced, track],
			),
			handleInputBlur: useCallback(() => {
				setLocalSearchTerm(null);
			}, []),
			selectLocation: useCallback(
				(selectedKey: string) => {
					if (!selectedKey) {
						return;
					}
					const location = locations.find((loc) => loc.value === selectedKey);
					if (!location) return;
					selectLocation(location.value, location.type, location.mergedValues);
					setLocalSearchTerm(null);
				},
				[locations, selectLocation],
			),
		},
	};
}
